import { useEffect, useRef, useState, useCallback } from "react";
import { Centrifuge } from "centrifuge";
import axiosInstance from "../../../axiosInstance";
import URL from "../../../urls";
import Bugsnag from "@bugsnag/js";

const AI_CHAT_CENTRIFUGO_TOKEN_SESSION_KEY = "aiChatCentrifugoToken";

/**
 * Hook to manage Centrifugo connection and subscription for AI Chat
 * Token is cached in sessionStorage and reused when available
 * Connection disconnects when hook is disabled or component unmounts
 *
 * @param {Object} options
 * @param {string} options.channel - The Centrifugo channel to subscribe to (from message response)
 * @param {boolean} options.enabled - Whether to enable the connection (default: true)
 * @param {Function} options.onMessage - Callback when message is received from channel
 * @param {Function} options.onError - Callback when error occurs
 * @param {Function} options.onStreamingComplete - Callback when streaming is complete (final message received)
 * @param {Function} options.onChannelCleared - Callback when channel is unsubscribed and cleared
 * @returns {Object} Connection and subscription status
 */
export function useAiChatCentrifugo({
  channel,
  enabled = true,
  onMessage,
  onError,
  onStreamingComplete,
  onChannelCleared,
}) {
  const clientRef = useRef(null);
  const subRef = useRef(null);
  const isRefreshingTokenRef = useRef(false);
  const lastTokenRef = useRef(null);
  const lastTokenSourceRef = useRef("none");
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [subscriptionStatus, setSubscriptionStatus] = useState("idle");

  // Store callbacks in refs so they never trigger effect re-runs
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onStreamingCompleteRef = useRef(onStreamingComplete);
  const onChannelClearedRef = useRef(onChannelCleared);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    onStreamingCompleteRef.current = onStreamingComplete;
  }, [onStreamingComplete]);
  useEffect(() => {
    onChannelClearedRef.current = onChannelCleared;
  }, [onChannelCleared]);

  const maskToken = useCallback(token => {
    if (!token || typeof token !== "string") return "<missing>";
    if (token.length <= 12) return token;
    return `${token.slice(0, 6)}...${token.slice(-6)}`;
  }, []);

  const isTokenExpiredError = useCallback(error => {
    if (!error) return false;

    if (error?.code === 109) return true;

    const message = String(error?.message || error || "").toLowerCase();
    return message.includes("token expired");
  }, []);

  const refreshTokenAndReconnect = useCallback(async () => {
    if (!clientRef.current || isRefreshingTokenRef.current) return;

    isRefreshingTokenRef.current = true;

    try {
      sessionStorage.removeItem(AI_CHAT_CENTRIFUGO_TOKEN_SESSION_KEY);
      lastTokenRef.current = null;
      lastTokenSourceRef.current = "forced-refresh";

      // Trigger a reconnect so Centrifugo requests a fresh token via getToken.
      clientRef.current.disconnect();
      clientRef.current.connect();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onErrorRef.current?.(err);
      Bugsnag.notify(err);
    } finally {
      isRefreshingTokenRef.current = false;
    }
  }, []);

  // Get token from session storage first; fetch from server only when missing.
  const getToken = useCallback(async () => {
    try {
      const sessionToken = sessionStorage.getItem(
        AI_CHAT_CENTRIFUGO_TOKEN_SESSION_KEY
      );

      if (sessionToken) {
        lastTokenRef.current = sessionToken;
        lastTokenSourceRef.current = "sessionStorage";
        return sessionToken;
      }

      const response = await axiosInstance.post(URL.aiChatCentrifugoToken);
      const token = response?.data?.token;

      if (!token) {
        throw new Error("No token received from server");
      }

      sessionStorage.setItem(AI_CHAT_CENTRIFUGO_TOKEN_SESSION_KEY, token);
      lastTokenRef.current = token;
      lastTokenSourceRef.current = "server";

      return token;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onErrorRef.current?.(err);
      Bugsnag.notify(err);
      throw err;
    }
  }, [maskToken]);

  // Initialize Centrifuge client
  useEffect(() => {
    if (!enabled) return;

    const initializeClient = async () => {
      try {
        setConnectionStatus("connecting");

        if (!clientRef.current) {
          clientRef.current = new Centrifuge(
            import.meta.env.VITE_CENTRIFUGO_SOCKET_URL ||
              "wss://rt.amaha.in/connection/websocket",
            {
              getToken: () => getToken(),
              debug: true,
            }
          );

          // Setup connection event listeners
          clientRef.current.on("connecting", () => {
            setConnectionStatus("connecting");
          });

          clientRef.current.on("connected", () => {
            setConnectionStatus("connected");
          });

          clientRef.current.on("disconnected", ctx => {
            if (ctx?.reason === "unauthorized") {
              sessionStorage.removeItem(AI_CHAT_CENTRIFUGO_TOKEN_SESSION_KEY);
            }

            setConnectionStatus("disconnected");
            setSubscriptionStatus("idle");
          });

          clientRef.current.on("error", ctx => {
            if (isTokenExpiredError(ctx?.error)) {
              refreshTokenAndReconnect();
              return;
            }

            const err =
              ctx.error instanceof Error
                ? ctx.error
                : new Error(String(ctx.error));
            onErrorRef.current?.(err);
          });

          // Connect the client
          await clientRef.current.connect();
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        Bugsnag.notify(err);
        setConnectionStatus("disconnected");
        onErrorRef.current?.(err);
      }
    };

    initializeClient();

    // Cleanup on unmount
    return () => {
      if (subRef.current && clientRef.current) {
        try {
          clientRef.current.removeSubscription(subRef.current);
          subRef.current = null;
          setSubscriptionStatus("idle");
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          Bugsnag.notify(err);
        }
      }
    };
  }, [
    enabled,
    getToken,
    isTokenExpiredError,
    refreshTokenAndReconnect,
    maskToken,
  ]);

  // Disconnect client when disabled (e.g. modal close) so reopen can reconnect fresh.
  useEffect(() => {
    if (enabled) return;

    if (subRef.current && clientRef.current) {
      try {
        clientRef.current.removeSubscription(subRef.current);
      } catch (error) {
        Bugsnag.notify(error);
      }
      subRef.current = null;
    }

    if (clientRef.current) {
      try {
        clientRef.current.disconnect();
      } catch (error) {
        Bugsnag.notify(error);
      }
      clientRef.current = null;
    }

    setConnectionStatus("disconnected");
    setSubscriptionStatus("idle");
  }, [enabled]);

  // Subscribe to channel
  useEffect(() => {
    if (!channel || !clientRef.current || connectionStatus !== "connected") {
      return;
    }

    const subscribe = async () => {
      try {
        setSubscriptionStatus("subscribing");

        // Remove old subscription if exists
        if (subRef.current) {
          clientRef.current?.removeSubscription(subRef.current);
          subRef.current = null;
        }

        // Create new subscription
        const sub = clientRef.current.newSubscription(channel);

        // Handle incoming messages
        sub.on("publication", ctx => {
          onMessageRef.current?.(ctx.data);

          // Check if this is the final message (streaming complete)
          // done: true signals the stream is complete — unsubscribe logic will be added later
          if (ctx.data?.done === true) {
            onStreamingCompleteRef.current?.(ctx.data);
          }
        });

        // Handle subscription events
        sub.on("subscribed", () => {
          setSubscriptionStatus("subscribed");
        });

        sub.on("error", ctx => {
          if (isTokenExpiredError(ctx?.error)) {
            refreshTokenAndReconnect();
            return;
          }

          const err =
            ctx.error instanceof Error
              ? ctx.error
              : new Error(String(ctx.error));
          onErrorRef.current?.(err);
        });

        sub.on("unsubscribed", () => {
          setSubscriptionStatus("idle");
        });

        subRef.current = sub;
        await sub.subscribe();
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        setSubscriptionStatus("idle");
        onErrorRef.current?.(err);
        Bugsnag.notify(err);
      }
    };

    subscribe();

    // Cleanup subscription only when channel or connection changes, not on callback changes
    return () => {
      if (subRef.current && clientRef.current) {
        try {
          clientRef.current.removeSubscription(subRef.current);
          subRef.current = null;
          setSubscriptionStatus("idle");
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          Bugsnag.notify(err);
        }
      }
    };
  }, [
    channel,
    connectionStatus,
    isTokenExpiredError,
    refreshTokenAndReconnect,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (subRef.current && clientRef.current) {
        try {
          clientRef.current.removeSubscription(subRef.current);
          subRef.current = null;
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          Bugsnag.notify(err);
        }
      }

      if (clientRef.current) {
        try {
          clientRef.current.disconnect();
          clientRef.current = null;
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          Bugsnag.notify(err);
        }
      }
    };
  }, []);

  // Function to manually unsubscribe from current channel
  const unsubscribeFromChannel = useCallback(() => {
    if (subRef.current && clientRef.current) {
      try {
        clientRef.current.removeSubscription(subRef.current);
        subRef.current = null;
        setSubscriptionStatus("idle");
        onChannelClearedRef.current?.();
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        Bugsnag.notify(err);
      }
    }
  }, []);

  return {
    connectionStatus,
    subscriptionStatus,
    client: clientRef.current,
    unsubscribeFromChannel,
  };
}
